import React from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

// Financial metrics definitions - Bilingual (English + French)
export const metricDefinitions = {
  // Stock-level metrics
  pe: {
    name: "P/E Ratio (Price to Earnings)",
    fullName: "RCB - Ratio Cours/Bénéfices",
    definition: "Prix de l'action ÷ Bénéfice par action. Indique combien tu paies pour 1€ de profit. Plus c'est bas, moins c'est cher."
  },
  pb: {
    name: "P/B Ratio (Price to Book)",
    fullName: "RCA - Ratio Cours/Actif Net",
    definition: "Prix de l'action ÷ Valeur comptable par action. Compare le prix de marché à la valeur des actifs nets. <1 = action sous-évaluée (en théorie)."
  },
  roe: {
    name: "ROE (Return on Equity)",
    fullName: "Rentabilité des Capitaux Propres",
    definition: "Résultat net ÷ Capitaux propres. Mesure la rentabilité des fonds investis par les actionnaires. >15% = bon."
  },
  marketCap: {
    name: "Market Cap",
    fullName: "Capitalisation Boursière",
    definition: "Prix de l'action × Nombre d'actions. Taille totale de l'entreprise en bourse."
  },
  debtEquity: {
    name: "D/E (Debt to Equity)",
    fullName: "Ratio d'Endettement",
    definition: "Dette totale ÷ Capitaux propres. Mesure l'endettement. <1 = peu endetté."
  },
  currentRatio: {
    name: "Current Ratio",
    fullName: "Ratio de Liquidité Générale",
    definition: "Actifs court terme ÷ Passifs court terme. Capacité à payer les dettes à court terme. >1.5 = confortable."
  },
  fcfYield: {
    name: "FCF Yield (Free Cash Flow)",
    fullName: "Rendement du Flux de Trésorerie Libre",
    definition: "Cash flow libre ÷ Market Cap. Rendement en cash réel généré. >5% = attractif."
  },
  dividendYield: {
    name: "Dividend Yield",
    fullName: "Rendement du Dividende",
    definition: "Dividende annuel ÷ Prix de l'action. Rendement du dividende versé aux actionnaires."
  },
  eps: {
    name: "EPS (Earnings Per Share)",
    fullName: "BPA - Bénéfice Par Action",
    definition: "Bénéfice net ÷ Nombre d'actions. Profit généré par action."
  },
  volume: {
    name: "Volume",
    fullName: "Volume d'Échange",
    definition: "Nombre d'actions échangées sur une période. Indique la liquidité du titre."
  },
  piotroski: {
    name: "Piotroski F-Score",
    fullName: "Score de Solidité Financière",
    definition: "Score de 0 à 9 basé sur 9 critères de santé financière (rentabilité, levier, liquidité). ≥7 = entreprise solide, ≤3 = fragile."
  },
  altmanZ: {
    name: "Altman Z''-Score",
    fullName: "Score de Risque de Faillite (Marchés Émergents)",
    definition: "Prédit le risque de faillite sur les marchés émergents. ≥2.6 = zone sûre, 1.1-2.6 = zone grise, <1.1 = zone de détresse."
  },
  beneish: {
    name: "Beneish M-Score",
    fullName: "Score de Manipulation Comptable",
    definition: "Détecte la manipulation des comptes. >-2.22 = risque élevé de manipulation, <-2.22 = comptes probablement fiables."
  },

  // Macroeconomic indicators
  gdpGrowth: {
    name: "GDP Growth (Gross Domestic Product)",
    fullName: "PIB - Produit Intérieur Brut",
    definition: "Croissance annuelle de la production économique du pays. >5% = croissance forte (émergents), 2-3% = normale (développés)."
  },
  inflationCpi: {
    name: "CPI Inflation (Consumer Price Index)",
    fullName: "IPC - Indice des Prix à la Consommation",
    definition: "Hausse générale des prix des biens et services. 2-3% = idéal, >5% = érosion du pouvoir d'achat."
  },
  centralBankRate: {
    name: "Central Bank Rate",
    fullName: "Taux Directeur de la Banque Centrale",
    definition: "Taux auquel la banque centrale prête aux banques commerciales. Influence le coût du crédit dans toute l'économie."
  },
  creditGrowth: {
    name: "Credit Growth",
    fullName: "Croissance du Crédit Bancaire",
    definition: "Expansion annuelle des prêts bancaires. >10% = économie dynamique mais attention à la surchauffe, <5% = ralentissement."
  },
  fdiInflows: {
    name: "FDI (Foreign Direct Investment)",
    fullName: "IDE - Investissements Directs Étrangers",
    definition: "Capitaux étrangers investis dans le pays. Indicateur de confiance et d'attractivité économique."
  },
  debtToGdp: {
    name: "Debt to GDP Ratio",
    fullName: "Ratio Dette Publique / PIB",
    definition: "Dette de l'État par rapport à la production nationale. <60% = soutenable, >100% = préoccupant."
  },
  unemployment: {
    name: "Unemployment Rate",
    fullName: "Taux de Chômage",
    definition: "Pourcentage de la population active sans emploi. <5% = plein emploi, >10% = problématique."
  },
  currentAccount: {
    name: "Current Account Balance",
    fullName: "Balance des Paiements Courants",
    definition: "Différence entre exportations et importations de biens/services. Positif = excédent, négatif = déficit."
  },
  realInterestRate: {
    name: "Real Interest Rate",
    fullName: "Taux d'Intérêt Réel",
    definition: "Taux nominal - Inflation. Indique le rendement réel de l'épargne. Négatif = l'épargne perd de la valeur."
  },
  urbanization: {
    name: "Urbanization Rate",
    fullName: "Taux d'Urbanisation",
    definition: "Part de la population vivant en ville. Croissance = demande en immobilier, infrastructure, consommation."
  },
  population: {
    name: "Population",
    fullName: "Population Totale",
    definition: "Nombre d'habitants. Plus la population est grande et jeune, plus le potentiel de croissance est élevé."
  },

  // Cycle phases
  cyclePhase: {
    name: "Economic Cycle Phase",
    fullName: "Phase du Cycle Économique",
    definition: "Position dans le cycle économique: Early (reprise) → Mid (expansion) → Late (pic) → Recession (contraction)."
  },
  expansionEarly: {
    name: "Early Expansion",
    fullName: "Début d'Expansion",
    definition: "Reprise après récession. Croissance accélère, chômage baisse, politique monétaire accommodante. Favorise cycliques et small-caps."
  },
  expansionMid: {
    name: "Mid-Cycle Expansion",
    fullName: "Milieu d'Expansion",
    definition: "Croissance soutenue. Profits des entreprises augmentent, crédit disponible. Phase idéale pour les actions."
  },
  expansionLate: {
    name: "Late-Cycle Expansion",
    fullName: "Fin d'Expansion",
    definition: "Économie proche du pic. Inflation monte, banques centrales resserrent. Favorise défensives et qualité."
  },
  recovery: {
    name: "Recovery Phase",
    fullName: "Phase de Reprise",
    definition: "Sortie de récession. Premiers signes d'amélioration, valorisations attractives. Opportunités d'achat."
  },

  // Freshness score
  freshnessScore: {
    name: "Data Freshness Score",
    fullName: "Score de Fraîcheur des Données",
    definition: "Mesure l'actualité des données. >90% = données récentes et fiables, <70% = données à vérifier pour plus de sécurité."
  },

  // Risk indicators
  riskHigh: {
    name: "High Risk",
    fullName: "Risque Élevé",
    definition: "Risque majeur pouvant impacter significativement les marchés. Surveillance active recommandée."
  },
  riskMedium: {
    name: "Medium Risk",
    fullName: "Risque Modéré",
    definition: "Risque à surveiller mais impact limité à court terme. Peut devenir élevé si la situation se dégrade."
  },
  riskLow: {
    name: "Low Risk",
    fullName: "Risque Faible",
    definition: "Risque identifié mais impact minimal attendu. À surveiller de façon périodique."
  },

  // Small-caps opportunity scores
  opportunityScore: {
    name: "Opportunity Score",
    fullName: "Score d'Opportunité",
    definition: "Note de 1 à 10 évaluant le potentiel d'un facteur pour les small-caps. ≥8 = excellent, 5-7 = bon, <5 = limité."
  },
  creditGrowthOpp: {
    name: "Credit Growth Opportunity",
    fullName: "Opportunité Croissance du Crédit",
    definition: "Disponibilité du financement bancaire pour les PME. Score élevé = prêts accessibles, expansion facilitée pour les small-caps."
  },
  urbanizationOpp: {
    name: "Urbanization Opportunity",
    fullName: "Opportunité Urbanisation",
    definition: "Migration vers les villes. Score élevé = demande croissante en infrastructure, immobilier, consommation, services."
  },
  fdiManufacturing: {
    name: "FDI Manufacturing Opportunity",
    fullName: "Opportunité IDE Industrie",
    definition: "Investissements étrangers dans l'industrie. Score élevé = supply chain local en expansion, sous-traitants small-caps bénéficiaires."
  },
  digitalPenetration: {
    name: "Digital Penetration Opportunity",
    fullName: "Opportunité Pénétration Digitale",
    definition: "Adoption internet/mobile. Score élevé = croissance e-commerce, fintech, services numériques. Opportunités pour les disrupteurs."
  },
  ftseUpgrade: {
    name: "FTSE/MSCI Upgrade Opportunity",
    fullName: "Opportunité Reclassification Indice",
    definition: "Reclassification vers 'Emerging Market'. Score élevé = afflux de capitaux passifs ($3-5Mds typique), revalorisation généralisée."
  },
  infrastructureSpend: {
    name: "Infrastructure Spending Opportunity",
    fullName: "Opportunité Dépenses Infrastructure",
    definition: "Investissements publics en routes, ports, énergie. Score élevé = contrats pour constructeurs et fournisseurs locaux."
  },
  demographicDividend: {
    name: "Demographic Dividend Opportunity",
    fullName: "Opportunité Dividende Démographique",
    definition: "Population jeune et croissante. Score élevé = main d'œuvre abondante, consommation en hausse, croissance structurelle."
  },
  exportDiversification: {
    name: "Export Diversification Opportunity",
    fullName: "Opportunité Diversification Exports",
    definition: "Variété des marchés d'export. Score élevé = résilience aux chocs, nouvelles opportunités commerciales."
  },
  realEstateGrowth: {
    name: "Real Estate Growth Opportunity",
    fullName: "Opportunité Croissance Immobilière",
    definition: "Dynamique du marché immobilier. Score élevé = promoteurs, matériaux de construction, services immobiliers en croissance."
  },
  techAdoption: {
    name: "Technology Adoption Opportunity",
    fullName: "Opportunité Adoption Technologique",
    definition: "Intégration des nouvelles technologies. Score élevé = startups tech, services IT, transformation digitale des entreprises."
  },
  consumerSpending: {
    name: "Consumer Spending Opportunity",
    fullName: "Opportunité Consommation des Ménages",
    definition: "Pouvoir d'achat des ménages. Score élevé = retail, FMCG, loisirs, services aux particuliers en expansion."
  },

  // Sector-related
  sectorBreakdown: {
    name: "Sector Breakdown",
    fullName: "Répartition Sectorielle",
    definition: "Distribution du PIB par secteur d'activité. Services = économie développée, Industrie = économie en développement."
  },

  // Catalyst
  catalyst: {
    name: "Market Catalyst",
    fullName: "Catalyseur de Marché",
    definition: "Événement ou tendance majeure pouvant déclencher une réévaluation du marché (upgrade MSCI, réformes, investissements massifs)."
  },

  // Macro Trends (OECD/World Bank)
  macroTrend: {
    name: "Macro Trend",
    fullName: "Tendance Macroéconomique",
    definition: "Direction de l'économie basée sur les 4 dernières périodes. Calculée à partir du PIB et de l'inflation pour donner un verdict global."
  },
  gdpTrend: {
    name: "GDP Trend",
    fullName: "Tendance du PIB",
    definition: "Évolution de la croissance sur les 4 derniers trimestres. ↗️ Accélération = dynamisme économique, ↘️ Décélération = ralentissement."
  },
  inflationTrend: {
    name: "Inflation Trend",
    fullName: "Tendance de l'Inflation",
    definition: "Évolution de l'inflation sur les 4 derniers mois. ↘️ En baisse = pouvoir d'achat préservé (positif), ↗️ En hausse = pression sur les marges."
  },
  combinedVerdict: {
    name: "Combined Verdict",
    fullName: "Verdict Macro Combiné",
    definition: "Synthèse GDP + Inflation. 🟢 Favorable = croissance + inflation maîtrisée. 🔴 Prudence = risque stagflation. 🟡 Neutre/Mixte = situation intermédiaire."
  },
  verdictFavorable: {
    name: "Favorable",
    fullName: "Contexte Favorable",
    definition: "PIB en accélération avec inflation stable ou en baisse. Environnement idéal pour l'investissement en actions."
  },
  verdictPrudence: {
    name: "Prudence",
    fullName: "Contexte de Prudence",
    definition: "PIB en décélération avec inflation en hausse = risque de stagflation. Favoriser les valeurs défensives et la qualité."
  },
  verdictNeutral: {
    name: "Neutral / Mixed",
    fullName: "Contexte Neutre ou Mixte",
    definition: "Signaux contradictoires ou stables. Sélectivité recommandée, privilégier l'analyse stock par stock."
  },
  dataConfidence: {
    name: "Data Confidence",
    fullName: "Niveau de Confiance des Données",
    definition: "Qualité de la source. Élevée = données trimestrielles/mensuelles OECD. Moyenne/Faible = données annuelles World Bank approximées."
  },
  dataSourceOECD: {
    name: "OECD Source",
    fullName: "Source OECD",
    definition: "Données de l'Organisation de Coopération et Développement Économiques. Haute fréquence (trimestrielle/mensuelle), très fiable."
  },
  dataSourceWorldBank: {
    name: "World Bank Source",
    fullName: "Source Banque Mondiale",
    definition: "Données de la Banque Mondiale. Fréquence annuelle approximée en périodes plus courtes. Moins précis mais couverture mondiale."
  },
  // Real Metrics (Phase 2)
  realPE: {
    name: "Real P/E",
    fullName: "P/E Ajusté Inflation",
    definition: "P/E nominal corrigé pour l'inflation locale vs USA (2%). Un P/E de 10 avec 5% d'inflation équivaut à un P/E réel de ~10.3. Permet des comparaisons internationales fiables."
  },
  realROE: {
    name: "Real ROE",
    fullName: "ROE Ajusté Inflation",
    definition: "ROE corrigé de la prime d'inflation locale. En haute inflation, les actifs sont sous-évalués au bilan, gonflant artificiellement le ROE. Real ROE = ROE nominal - (inflation locale - 2%)."
  },
  realGrowth: {
    name: "Real Growth",
    fullName: "Croissance Réelle",
    definition: "Croissance du CA/bénéfices moins l'inflation. 15% nominal avec 5% d'inflation = 10% réel. Mesure la vraie création de valeur."
  },
  earningsYieldSpread: {
    name: "Earnings Yield Spread",
    fullName: "Spread Rendement Bénéfices",
    definition: "Rendement bénéfices (1/PE) moins taux sans risque local. Spread positif = action attractive vs obligations. Spread négatif = action chère."
  },
  marketCapUSD: {
    name: "Market Cap USD",
    fullName: "Capitalisation en USD",
    definition: "Capitalisation boursière convertie en USD pour comparaison internationale. Nano <$50M, Micro <$300M, Small <$2B, Mid <$10B, Large >$10B."
  },
  realValuationScore: {
    name: "Real Valuation Score",
    fullName: "Score Valorisation Réelle",
    definition: "Score combiné (0-100) basé sur P/E réel, ROE réel et spread rendement. Permet de classer des actions de différents pays sur une base comparable."
  },
  // Accounting Quality (Phase 2)
  beneishMScore: {
    name: "Beneish M-Score",
    fullName: "Score Beneish M",
    definition: "Détecte la manipulation des résultats. M < -2.22 = manipulation peu probable. M ≥ -2.22 = risque élevé de manipulation. Basé sur 8 ratios financiers (DSRI, GMI, AQI, SGI, DEPI, SGAI, LVGI, TATA)."
  },
  altmanZScore: {
    name: "Altman Z''-Score",
    fullName: "Score Altman Z'' (Marchés Émergents)",
    definition: "Prédit le risque de faillite (version marchés émergents). Z ≥ 2.6 = sûr. 1.1 ≤ Z < 2.6 = zone grise. Z < 1.1 = détresse financière."
  },
  piotroskiFScore: {
    name: "Piotroski F-Score",
    fullName: "Score Piotroski F",
    definition: "Évalue la solidité financière (0-9). 9 tests binaires: rentabilité, levier, liquidité, efficacité. 8-9 = excellent. 0-3 = éviter."
  },
  cashFlowQuality: {
    name: "Cash Flow Quality",
    fullName: "Qualité Cash Flow",
    definition: "Compare bénéfices comptables vs cash réel. CFO < Net Income = qualité médiocre. Accruals élevés = risque manipulation. CFO négatif + bénéfices positifs = ALERTE."
  },
  accountingQuality: {
    name: "Accounting Quality",
    fullName: "Qualité Comptable Globale",
    definition: "Score combiné Beneish M-Score + Altman Z''-Score + Piotroski F-Score + Cash Flow. 🔴 = drapeaux rouges (manipulation, faillite). 🟢 = fondamentaux sains et transparents."
  }
};

// MetricTooltip component - shows a "?" icon with tooltip on hover
export const MetricTooltip = ({ metric, children, className = "" }) => {
  const def = metricDefinitions[metric];
  
  if (!def) {
    return children || null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex items-center gap-1 cursor-help ${className}`}>
            {children || def.name}
            <HelpCircle className="w-3.5 h-3.5 text-gray-500 hover:text-emerald-400 transition-colors" />
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs bg-gray-900 border border-white/10 text-white p-3 rounded-lg shadow-xl"
        >
          <div className="space-y-1">
            <div className="font-semibold text-emerald-400 text-sm">
              {def.name} <span className="font-normal text-gray-400">({def.fullName})</span>
            </div>
            <p className="text-xs text-gray-300 leading-relaxed">
              {def.definition}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// MetricLabel component - just the label with tooltip (for headers)
export const MetricLabel = ({ metric, label }) => {
  const def = metricDefinitions[metric];
  
  return (
    <MetricTooltip metric={metric}>
      {label || def?.name || metric}
    </MetricTooltip>
  );
};

export default MetricTooltip;
